import React, { useEffect, useRef, useState } from "react";
import { Box, CssBaseline, Toolbar } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header_new";
import AppSidebar from "./UnifiedSidebar";
import { useAuth, useCurrentTalentProfile } from "../hooks";
import SocketHandler from "../utils/socketHandler";
import {
  getGTPUserSocketNotification,
  getTalentSocketNotification,
  getUserSocketNotification,
} from "../utils/notificationHandler";
import { playNotificationSound } from "../utils/notificationSound";
import useCurrentGTP from "../hooks/useGTP";
import { useNotificationStore } from "../services/store/useNotificationStore";
import { useLaunchStore } from "../services/store/useLaunchStore";
import useThemeStore from "../services/store/useThemeStore";
import {
  AIRBANK_DARK_CANVAS,
  AIRBANK_LIGHT_CANVAS,
} from "../constants/airbankSurfaces";

const MainLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentProfile } = useCurrentTalentProfile();
  const { currentGTP } = useCurrentGTP();

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const CreateCard = () =>
    currentProfile?.user?.talent
      ? navigate("/create-card")
      : navigate("/create-card");

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const buildPath = (index: number) =>
    "/" + pathSegments.slice(0, index + 1).join("/");

  const [badgeVisible, setBadgeVisible] = useState(false);

  const [socketNotification, setSocketNotification] = useState<any>([]);
  const { userInfo } = useAuth();
  const { setCurrentNotification } = useNotificationStore();
  const socketSubscribedRef = useRef(false);
  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current || !userInfo?.id) {
      return;
    }

    try {
      // Get socket instance and subscribe to messages
      const socketHandler = SocketHandler.getInstance();

      socketHandler.subscribeToNotification(
        userInfo.id.toString(),
        (message) => {
          if (message) {
            const notification = currentGTP?.id
              ? getGTPUserSocketNotification(message, currentGTP?.id)
              : currentProfile?.id
                ? getTalentSocketNotification(message, currentProfile?.id)
                : getUserSocketNotification(message);
            console.log("notification are here in main layout", notification);
            if (notification) {
              setSocketNotification((prev: any) => [...prev, notification]);
              setBadgeVisible(true);
              setCurrentNotification(notification);
              // Play notification sound
              playNotificationSound();
            }
          }
          // Handle the message here
        },
      );

      socketSubscribedRef.current = true;
     
    } catch (error) {
     
    }

    // Cleanup subscription when component unmounts
    return () => {
      try {
        if (userInfo?.id) {
          const socketHandler = SocketHandler.getInstance();
          socketHandler.unsubscribeFromNotification(userInfo.id.toString());
          socketSubscribedRef.current = false;
          console.log("Successfully unsubscribed from notification socket");
        }
      } catch (error) {
        console.error("Failed to unsubscribe from socket:", error);
      }
    };
  }, [userInfo?.id, currentGTP?.id, currentProfile?.id]);

  const { darkMode: isDarkMode } = useThemeStore();

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
      }}
    >
      <CssBaseline />

      <GlobalLaunchOverlay />

      {/* Header and Sidebar */}
      <Header
        toggleDrawer={toggleDrawer}
        onClickCreate={CreateCard}
        setBadgeVisible={setBadgeVisible}
        badgeVisible={badgeVisible}
        socketNotification={socketNotification}
      />
      <AppSidebar open={drawerOpen} setDrawerOpen={setDrawerOpen} />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: isDarkMode ? AIRBANK_DARK_CANVAS : AIRBANK_LIGHT_CANVAS,
          px: { xs: 1, md: 3 },
          py: 3,
          overflow: "hidden",
        }}
      >
        <Toolbar />

        {/* Breadcrumb Navigation */}
        {/* <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer" }}
          >
            Home
          </MuiLink>

          {pathSegments.map((seg, idx) => {
            const isLast = idx === pathSegments.length - 1;
            const label = seg.charAt(0).toUpperCase() + seg.slice(1);

            return isLast ? (
              <Typography key={idx} color="text.primary">
                {label}
              </Typography>
            ) : (
              <MuiLink
                key={idx}
                underline="hover"
                color="inherit"
                onClick={() => navigate(buildPath(idx))}
                sx={{ cursor: "pointer" }}
              >
                {label}
              </MuiLink>
            );
          })}
        </Breadcrumbs> */}

        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

// ── Global launch overlay — rendered ABOVE the Outlet so it survives navigation ─
const GlobalLaunchOverlay: React.FC = () => {
  const { payload, clear } = useLaunchStore();
  const [expanded, setExpanded] = useState(false);
  const prevPayloadRef = useRef<typeof payload>(null);

  useEffect(() => {
    if (!payload) {
      setExpanded(false);
      return;
    }
    // Only kick off the expansion when a new payload arrives
    if (payload !== prevPayloadRef.current) {
      prevPayloadRef.current = payload;
      setExpanded(false);
      let id1: number, id2: number;
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setExpanded(true));
      });
      // Auto-clear after animation + navigation settle
      const t = setTimeout(() => clear(), 500);
      return () => {
        cancelAnimationFrame(id1);
        cancelAnimationFrame(id2);
        clearTimeout(t);
      };
    }
  }, [payload, clear]);

  if (!payload) return null;

  const { bg, glowColor, iconColor, icon, originX, originY, startRadius } =
    payload;
  const endR = Math.hypot(window.innerWidth, window.innerHeight) + 50;
  const ease = "cubic-bezier(0.32, 0.72, 0, 1)";
  const dur = "0.46s";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: bg,
        pointerEvents: "none",
        clipPath: expanded
          ? `circle(${endR}px at ${originX}px ${originY}px)`
          : `circle(${startRadius}px at ${originX}px ${originY}px)`,
        transition: expanded ? `clip-path ${dur} ${ease}` : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(140deg, rgba(255,255,255,0.20) 0%, transparent 40%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: originY,
          left: originX,
          transform: expanded
            ? "translate(-50%,-50%) scale(3.5)"
            : "translate(-50%,-50%) scale(1)",
          opacity: expanded ? 0 : 1,
          transition: expanded
            ? `transform ${dur} ${ease}, opacity 0.25s ease`
            : "none",
          color: iconColor,
          filter: `drop-shadow(0 0 20px ${glowColor})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

export default MainLayout;
